import { ibhApiService } from '@hub/bearing-hub/common/api-service';
import type { IbhShape } from '@igus/iglidur-bearing-hub-api';
import type { Session } from '@hub/hooks/states/useSessionStore';
import type { IbhDimensions } from '@hub/bearing-hub/common/dimensions/dimensions';
import type {
  IbhCatalogArticleResponseDto,
  IbhCustomArticleParams,
  IbhCustomArticleResponseDto,
} from '@igus/iglidur-bearing-hub-api/src/data-contracts';
import { IbhProductionMethods } from '@igus/iglidur-bearing-hub-api/src/data-contracts';
import * as Sentry from '@sentry/nextjs';
import type { UnitSystem } from '@igus/kopla-domain';

type ArticleProps = {
  articleNumber: string;
  isCustom: boolean;
  quantity: number;
  shapeKey: IbhShape;
  dimensions?: IbhDimensions;
  unitSystem: UnitSystem;
  session: Session;
  productionMethod: IbhProductionMethods;
};

export const Article = ({
  articleNumber,
  isCustom,
  quantity,
  shapeKey,
  session,
  unitSystem,
  dimensions,
  productionMethod,
}: ArticleProps) => {
  const isCustomArticle = (
    article: IbhCustomArticleResponseDto | IbhCatalogArticleResponseDto
  ): article is IbhCustomArticleResponseDto => 'customArticleNumber' in article;

  const getArticleIdToFetchDataFromAkeneo = (
    articleData: IbhCustomArticleResponseDto | IbhCatalogArticleResponseDto
  ): {
    articleId: string;
    haveCatalogNumber: boolean;
  } => {
    const articleId = isCustomArticle(articleData)
      ? articleData.catalogArticleNumber || articleData.customArticleNumber // use Article number to similar article in the catalog
      : articleData.articleNumber;
    const haveCatalogNumber = isCustomArticle(articleData) && !!articleData.catalogArticleNumber;
    return { articleId, haveCatalogNumber };
  };

  const fetchArticleData = async (): Promise<IbhCustomArticleResponseDto | IbhCatalogArticleResponseDto> => {
    const { sessionId, server } = session ?? {};
    const useIcn = productionMethod === IbhProductionMethods.SEMIFINISHED;
    const customArticleParams = {
      ...dimensions,
      articleNumber,
      quantity,
      shape: shapeKey,
      unitSystem,
      sid: sessionId,
    } as IbhCustomArticleParams;

    const catalogArticleParams = {
      articleNumber,
      quantity,
      sid: sessionId,
      server,
    };
    const customArticlePromise = useIcn ? ibhApiService.icnArticle : ibhApiService.customArticle;
    try {
      const response = isCustom
        ? await customArticlePromise(customArticleParams)
        : await ibhApiService.catalogArticle(catalogArticleParams);

      return response.data;
    } catch (error) {
      Sentry.captureException(error, {
        contexts: {
          request: {
            isCustom,
            articleNumber,
            quantity,
            shapeKey,
            unitSystem,
            sessionId,
            dimensions,
          },
        },
        tags: {
          // eslint-disable-next-line no-nested-ternary
          api_call: isCustom ? (useIcn ? 'icnArticle' : 'customArticle') : 'catalogArticle',
          article_type: isCustom ? 'custom' : 'catalog',
        },
        level: 'error',
      });
      return undefined;
    }
  };
  return { fetchArticleData, isCustomArticle, getArticleIdToFetchDataFromAkeneo };
};
